/*
 * Cart shipping estimator (cart page).
 * Auto-fetches the cheapest shipping rate for the selected country and writes it
 * as a line in the cart totals ("Estimated shipping: X" / "Free"). No zip, no
 * province, no button. The country choice persists per session and re-fetches
 * after cart re-renders, so the free-shipping state stays in sync with the cart
 * total (e.g. flips to "Free" once the cart crosses the free-shipping threshold).
 *
 * The native country <select> is progressively enhanced into an accessible,
 * searchable combobox (SearchableSelect). Without JS the native select still
 * works; the combobox drives the same native select, so the fetch logic below is
 * untouched.
 */
(function () {
  var SESSION_KEY = 'cart-shipping-country';
  var uid = 0;

  function money(price, format) {
    var cents = Math.round(parseFloat(price) * 100);
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, format);
    }
    return price;
  }

  var CARET =
    '<svg viewBox="0 0 12 8" width="12" height="8" fill="none" aria-hidden="true">' +
    '<path d="M1 1.5 6 6.5l5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Accessible searchable dropdown built on top of a native <select>. */
  class SearchableSelect {
    constructor(select, strings) {
      this.select = select;
      this.strings = strings || {};
      this.id = 'csel-' + ++uid;
      this.active = null;
      this.onDocPointer = this.onDocPointer.bind(this);
      this.build();
    }

    build() {
      var nativeWrap = this.select.closest('.select') || this.select.parentElement;
      this.nativeWrap = nativeWrap;

      this.root = document.createElement('div');
      this.root.className = 'csel';

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'csel__trigger form-control form-control--select';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.label = document.createElement('span');
      this.label.className = 'csel__label';
      this.trigger.appendChild(this.label);

      var caret = document.createElement('span');
      caret.className = 'csel__caret';
      caret.innerHTML = CARET;

      this.panel = document.createElement('div');
      this.panel.className = 'csel__panel';
      this.panel.hidden = true;

      this.search = document.createElement('input');
      this.search.type = 'text';
      this.search.className = 'csel__search form-control';
      this.search.setAttribute('role', 'combobox');
      this.search.setAttribute('aria-autocomplete', 'list');
      this.search.setAttribute('aria-expanded', 'true');
      this.search.setAttribute('aria-controls', this.id + '-list');
      this.search.setAttribute('autocomplete', 'off');
      this.search.setAttribute('spellcheck', 'false');
      if (this.strings.search) {
        this.search.placeholder = this.strings.search;
        this.search.setAttribute('aria-label', this.strings.search);
      }

      this.list = document.createElement('ul');
      this.list.className = 'csel__list';
      this.list.id = this.id + '-list';
      this.list.setAttribute('role', 'listbox');

      this.options = [];
      var seen = {};
      Array.from(this.select.options).forEach(function (opt, i) {
        // Skip empty options and Shopify's "---" divider; dedupe the country
        // that Shopify hoists to the top (it also appears alphabetically).
        if (!opt.value || opt.value === '---') return;
        if (seen[opt.value]) return;
        seen[opt.value] = true;
        var li = document.createElement('li');
        li.className = 'csel__option';
        li.id = this.id + '-opt-' + i;
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.textContent = opt.textContent;
        if (opt.value === this.select.value) li.setAttribute('aria-selected', 'true');
        this.list.appendChild(li);
        this.options.push(li);
      }, this);

      this.empty = document.createElement('li');
      this.empty.className = 'csel__empty';
      this.empty.setAttribute('role', 'presentation');
      this.empty.hidden = true;
      this.empty.textContent = this.strings.noResults || '';
      this.list.appendChild(this.empty);

      this.panel.appendChild(this.search);
      this.panel.appendChild(this.list);
      this.root.appendChild(this.trigger);
      this.root.appendChild(caret);
      this.root.appendChild(this.panel);

      nativeWrap.style.display = 'none';
      nativeWrap.setAttribute('aria-hidden', 'true');
      nativeWrap.insertAdjacentElement('afterend', this.root);

      this.syncLabel();
      this.bind();
    }

    syncLabel() {
      var opt = this.select.options[this.select.selectedIndex];
      this.label.textContent = opt ? opt.textContent : '';
    }

    bind() {
      this.trigger.addEventListener('click', this.toggle.bind(this));
      this.trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open();
        }
      }.bind(this));

      this.search.addEventListener('input', function () {
        this.filter(this.search.value);
      }.bind(this));
      this.search.addEventListener('keydown', this.onSearchKey.bind(this));

      this.list.addEventListener('click', function (e) {
        var li = e.target.closest('.csel__option');
        if (li && !li.hidden) this.choose(li);
      }.bind(this));
      this.list.addEventListener('mousemove', function (e) {
        var li = e.target.closest('.csel__option');
        if (li && !li.hidden && li !== this.active) this.setActive(li);
      }.bind(this));
    }

    visibleOptions() {
      return this.options.filter(function (li) {
        return !li.hidden;
      });
    }

    filter(query) {
      var q = (query || '').trim().toLowerCase();
      var visible = 0;
      this.options.forEach(function (li) {
        var match = li.textContent.toLowerCase().indexOf(q) !== -1;
        li.hidden = !match;
        if (match) visible++;
      });
      this.empty.hidden = visible !== 0;
      var vis = this.visibleOptions();
      this.setActive(vis.length ? vis[0] : null);
    }

    setActive(li) {
      if (this.active) this.active.classList.remove('is-active');
      this.active = li;
      if (li) {
        li.classList.add('is-active');
        this.search.setAttribute('aria-activedescendant', li.id);
        li.scrollIntoView({ block: 'nearest' });
      } else {
        this.search.removeAttribute('aria-activedescendant');
      }
    }

    move(dir) {
      var vis = this.visibleOptions();
      if (!vis.length) return;
      var idx = this.active ? vis.indexOf(this.active) : -1;
      idx = (idx + dir + vis.length) % vis.length;
      this.setActive(vis[idx]);
    }

    onSearchKey(e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.move(-1);
          break;
        case 'Enter':
          e.preventDefault();
          if (this.active) this.choose(this.active);
          break;
        case 'Escape':
          e.preventDefault();
          this.close();
          this.trigger.focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    choose(li) {
      this.options.forEach(function (o) {
        o.removeAttribute('aria-selected');
      });
      li.setAttribute('aria-selected', 'true');
      this.select.value = li.dataset.value;
      this.label.textContent = li.textContent;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
      this.trigger.focus();
    }

    toggle() {
      if (this.panel.hidden) this.open();
      else this.close();
    }

    open() {
      if (!this.panel.hidden) return;
      this.panel.hidden = false;
      this.root.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.search.value = '';
      this.filter('');
      var selected = this.options.find(function (o) {
        return o.getAttribute('aria-selected') === 'true';
      });
      if (selected) this.setActive(selected);
      this.search.focus();
      document.addEventListener('pointerdown', this.onDocPointer, true);
    }

    close() {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.root.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onDocPointer, true);
    }

    onDocPointer(e) {
      if (!this.root.contains(e.target)) this.close();
    }

    destroy() {
      document.removeEventListener('pointerdown', this.onDocPointer, true);
    }
  }

  class CartShippingEstimator extends HTMLElement {
    constructor() {
      super();
      this.select = this.querySelector('select');
      this.lineSelector = this.getAttribute('data-line-target') || '[data-cart-shipping-line]';
      this.freeLabel = this.getAttribute('data-free-label') || 'Free';
      this.defaultCountry = this.getAttribute('data-default-country') || '';
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      if (!this.select) return;
      this.restoreCountry();
      // A single fixed country (Saudi Arabia) needs no dropdown.
      if (this.select.options.length > 1) {
        this.combobox = new SearchableSelect(this.select, {
          search: this.getAttribute('data-search-label') || '',
          noResults: this.getAttribute('data-no-results-label') || '',
        });
      }
      this.select.addEventListener('change', this.onChange);
      // Auto-calc on load for the current selection.
      this.fetchRate();
    }

    disconnectedCallback() {
      if (this.select) this.select.removeEventListener('change', this.onChange);
      if (this.combobox) this.combobox.destroy();
    }

    restoreCountry() {
      var saved = null;
      try {
        saved = sessionStorage.getItem(SESSION_KEY);
      } catch (e) {}
      var target = saved || this.defaultCountry;
      if (!target) return;
      var match = Array.from(this.select.options).find(function (o) {
        return o.value === target;
      });
      if (match) this.select.value = target;
    }

    onChange() {
      try {
        sessionStorage.setItem(SESSION_KEY, this.select.value);
      } catch (e) {}
      this.fetchRate();
    }

    line() {
      var root = this.closest('main-cart') || document;
      return root.querySelector(this.lineSelector);
    }

    setLoading(state) {
      this.classList.toggle('is-loading', !!state);
    }

    hideLine() {
      var line = this.line();
      if (!line) return;
      line.hidden = true;
      line.classList.remove('cart-shipping-line--free');
      var valueEl = line.querySelector('[data-cart-shipping-value]');
      if (valueEl) valueEl.textContent = '';
    }

    fetchRate() {
      if (!window.FoxTheme || !FoxTheme.routes) return;
      var country = this.select.value;
      if (!country) {
        this.hideLine();
        return;
      }
      var url = (FoxTheme.routes.cart_url + '/shipping_rates.json').replace('//', '/');
      var body = JSON.stringify({ shipping_address: { country: country } });
      this.setLoading(true);
      fetch(url, Object.assign({}, FoxTheme.utils.fetchConfig('javascript'), { body: body }))
        .then(function (response) {
          return response.json();
        })
        .then(this.render.bind(this))
        .catch(this.hideLine.bind(this))
        .finally(this.setLoading.bind(this, false));
    }

    render(state) {
      var rates = state && state.shipping_rates;
      var line = this.line();
      if (!line || !rates || !rates.length) {
        this.hideLine();
        return;
      }
      var cheapest = rates.reduce(function (min, rate) {
        return parseFloat(rate.price) < parseFloat(min.price) ? rate : min;
      });
      var valueEl = line.querySelector('[data-cart-shipping-value]');
      if (!valueEl) {
        this.hideLine();
        return;
      }
      var price = parseFloat(cheapest.price);
      var format = (window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat) || undefined;
      if (price === 0) {
        line.classList.add('cart-shipping-line--free');
        valueEl.textContent = this.freeLabel;
      } else {
        line.classList.remove('cart-shipping-line--free');
        valueEl.textContent = money(cheapest.price, format);
      }
      line.hidden = false;
    }
  }

  if (!customElements.get('cart-shipping-estimator')) {
    customElements.define('cart-shipping-estimator', CartShippingEstimator);
  }
})();
